import { useNavigate } from "react-router-dom";
import { HStack, Error } from "../../../shared/utils";
import { Input, Label, Required } from "../../../shared/InputField";
import { useState } from "react";
import { Icon } from "@iconify/react";
import * as Yup from "yup";
import PasswordStrengthIndicator from "../../../pages/auth/passwordIndicator";
import {
  ButtonSize,
  PrimaryCTAButton,
  TransparentCTAButton,
} from "../../../shared/CTAButtonComponents";
import { InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Dialog } from "@mui/material";
import { useFormik } from "formik";
import { useChangePassword, useCreatePassword } from "../../../queries/auth";
import { toast } from "react-toastify";
import { useAuthStore } from "../../../store/useAuthStore";
import { handleClickForTracking } from "../../../analytics";
import { AnalyticsConstant } from "../../../AnalyticsContant";
import { trackEvent } from "../../../utils/AmpAnalytics";
import { checkHeartBeat } from "../../../api/auth";

export type ChangePasswordType = {
  password: string;
  newPassword: string;
  confirmNewPassword: string;
};

export default function ChangePasswordModal({
  isOpen,
  onClose,
  hasPassword = true,
  onPasswordUpdated,
}: {
  isOpen: boolean;
  onClose: () => void;
  hasPassword?: boolean;
  onPasswordUpdated?: () => void;
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const {mutate: ChangePassword} = useChangePassword();
  const {mutate: CreatePassword} = useCreatePassword();
    const { accessToken } = useAuthStore();

  const initialValue: ChangePasswordType = {
    password: "",
    newPassword: "",
    confirmNewPassword: "",
  };

  const ChangePasswordValidationSchema = Yup.object().shape({
    password: hasPassword
      ? Yup.string().required("Old Password is required")
      : Yup.string().notRequired(),
    newPassword: Yup.string()
      .min(6, "Password length should be at least 6")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{6,})/,
        "Must Include at least 6 Characters, One Uppercase, One Lowercase, One Number and One Special Character"
      )
      .max(50, "Maximum 50 Characters")
      .required("Password is required"),
    confirmNewPassword: Yup.string()
      .required("Password confirmation is required")
      .oneOf(
        [Yup.ref("newPassword")],
        "Confirm Password doesn't match the entered Password."
      ),
  });

  const formik = useFormik<ChangePasswordType>({
    initialValues: initialValue,
    validationSchema: ChangePasswordValidationSchema,
    onSubmit: async (values) => {
      // handleClickForTracking(AnalyticsConstant.change_password_button);
      trackEvent("Changing Password");
      setLoading(false);
            const onSuccess = async () => {
              setLoading(false);
              toast(hasPassword ? "Password Updated Successfully" : "Password Created Successfully", {
                type: "success",
                autoClose: 2000,
              });
              const heartbeat = await checkHeartBeat();
              if (!heartbeat) return;

              onPasswordUpdated?.();
              onClose();
            };
            const onError = (error: any) => {
              toast(error?.response?.data?.message || "Something went wrong!", {
                type: "error",
                autoClose: 2000,
              });
              setLoading(false);
            };

            if (hasPassword) {
              ChangePassword({
                data: {
                  password: values.password,
                  newPassword: values.newPassword,
                  confirmNewPassword: values.confirmNewPassword,
                },
                token: `${accessToken}`,
              }, { onSuccess, onError });
            } else {
              CreatePassword({
                data: {
                  newPassword: values.newPassword,
                  confirmNewPassword: values.confirmNewPassword,
                },
              }, { onSuccess, onError });
            }
    },
  });

  return (
    <>
      <Dialog open={isOpen} maxWidth="lg" onClose={onClose}>
        <HStack className="p-9 py-6 flex flex-nowrap justify-between w-100">
          <p className="text-lg text-gray-600 font-semibold">
            {hasPassword ? "Change Password" : "Create Password"}
          </p>
          <button
            type="button"
            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            onClick={() => {
              onClose();
            }}
          >
            <span className="sr-only">Close</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </HStack>
        <div className="border-t border-gray-200"></div>

        <form
          className="p-9 py-4"
          onSubmit={formik.handleSubmit}
        >
          {hasPassword && <div className="">
            <div className="mx-auto w-full md:w-[400px]">
              <Label>
                Old Password <Required />
              </Label>
              <div className="relative">
                <Input
                  className="w-full pr-10"
                  type={showOldPassword ? "text" : "password"}
                  placeholder="Old Password"
                    {...formik.getFieldProps("password")}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 mt-2 right-3 flex items-center text-gray-500"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                >
                  {!showOldPassword ? (
                    <Icon
                      icon="garden:eye-hide-fill-12"
                      className="text-gray-400 placeholderLoader rounded-lg"
                      height={18}
                    />
                  ) : (
                    <Icon
                      icon="subway:eye"
                      className="text-gray-400 placeholderLoader rounded-lg"
                      height={18}
                    />
                  )}
                </button>
              </div>

              {formik.touched?.password && formik.errors?.password && (
                <Error
                  className="text-left md:w-[400px] mx-auto mt-1 text-red-500"
                  text={formik.errors.password}
                />
              )}
            </div>
          </div>}

          <div className="mt-4">
            <>
              <div className="mx-auto w-full md:w-[400px]">
                <Label>
                  New Password <Required />
                </Label>
                <div className="relative">
                  <Input
                    className="w-full pr-10"
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                      {...formik.getFieldProps("newPassword")}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 mt-2 right-3 flex items-center text-gray-500"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {!showPassword ? (
                      <Icon
                        icon="garden:eye-hide-fill-12"
                        className="text-gray-400 placeholderLoader rounded-lg"
                        height={18}
                      />
                    ) : (
                      <Icon
                        icon="subway:eye"
                        className="text-gray-400 placeholderLoader rounded-lg"
                        height={18}
                      />
                    )}
                  </button>
                </div>

                {formik.touched?.newPassword && formik.errors?.newPassword && (
                <Error
                  className="text-left md:w-[400px] mx-auto mt-1 text-red-500"
                  text={formik.errors.newPassword}
                />
              )}
              </div>
              <div className="mx-auto w-full md:w-[400px] mt-4">
                <Label>
                  Confirm Password <Required />
                </Label>
                <div className="relative">
                  <Input
                    className="w-full pr-10"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm Password"
                      {...formik.getFieldProps("confirmNewPassword")}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 mt-2 right-3 flex items-center text-gray-500"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {!showConfirmPassword ? (
                      <Icon
                        icon="garden:eye-hide-fill-12"
                        className="text-gray-400 placeholderLoader rounded-lg"
                        height={18}
                      />
                    ) : (
                      <Icon
                        icon="subway:eye"
                        className="text-gray-400 placeholderLoader rounded-lg"
                        height={18}
                      />
                    )}
                  </button>
                </div>

                {formik.touched?.confirmNewPassword &&
                formik.errors?.confirmNewPassword && (
                  <Error
                    className="text-left md:w-[400px] mx-auto mt-1 text-red-500"
                    text={formik.errors.confirmNewPassword}
                  />
                )}
              </div>
              <PasswordStrengthIndicator password={formik.values.newPassword} />
            </>
          </div>

          <div
            className="mx-auto mt-6 flex w-full items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 md:w-[400px]"
            role="note"
          >
            <InformationCircleIcon className="h-4 w-4 shrink-0" />
            <p>
              You’ll be signed out after changing your password. Please sign in
              again with your new password.
            </p>
          </div>
          <div className="mt-4 flex justify-end gap-4">
            <TransparentCTAButton
              className="w-auto text-center px-auto rounded-md"
              buttonSize={ButtonSize.MEDIUM}
              type="button"
              onClick={() => onClose()}
            >
              <p className="w-full text-sm leading-0">{"Cancel"}</p>
            </TransparentCTAButton>

            <PrimaryCTAButton
              event-name="reset Password"
              className="w-28 text-center px-auto "
              buttonSize={ButtonSize.MEDIUM}
              loading={loading}
              type="submit"
            >
              <p className="w-full text-sm leading-0">{"Confirm"}</p>
            </PrimaryCTAButton>
          </div>
        </form>
      </Dialog>
    </>
  );
}
